import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/ui/Toast';
import {
  useCampaign,
  useCampaignSequence,
  useSaveCampaignSequence,
  type SequenceStep,
} from '../../api/campaigns.api';
import { useTemplates, type Template } from '../../api/templates.api';
import {
  FormCard,
  FormHeader,
  WizardLayout,
  WizardFooter,
  enrollmentMode,
  stepInfo,
  stepPath,
} from './builder-shell';
import { MergeFieldInserter } from '../templates/builder/MergeFieldInserter';
import { COMMON_MERGE_TOKENS, SMS_MERGE_TOKENS, TASK_MERGE_TOKENS } from '../../lib/merge-tokens';
import { useOrgUsers } from '../../api/org-users.api';
import { useOrgSettings } from '../../api/org-settings.api';
import { smsSegments, withOptOutFooter } from '../../lib/sms-text';
import { TaskAssigneeSelect, TokenField } from './step-fields';

/* ── Screen 03 — Campaign builder · Step 3 Sequence ── */

const MAX_STEPS = 7;

type StepType = SequenceStep['type'];

const STEP_TYPE_OPTIONS: { id: StepType; label: string; description: string }[] = [
  { id: 'EMAIL',  label: 'Email',  description: 'Send an email to the enrolled contact.' },
  { id: 'SMS',    label: 'SMS',    description: 'Send a text message via the campaign’s bound Twilio number.' },
  { id: 'DELAY',  label: 'Delay',  description: 'Wait N business days before the next step.' },
  { id: 'TASK',   label: 'Task',   description: 'Create a task for the recruiter to action.' },
  { id: 'BRANCH', label: 'Branch', description: 'Route based on reply/open/click activity.' },
  { id: 'EXIT',   label: 'Exit',   description: 'End the sequence for this enrollment.' },
];

// SMS body cap mirrors backend SMS_BODY_MAX in sequences.service.ts. The
// segment count (lib/sms-text) warns authors when a text costs 2+ messages.
const SMS_BODY_MAX = 480;

export function CampaignSequencePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: campaign, isLoading: loadingCampaign } = useCampaign(id);
  const { data: existing, isLoading: loadingSeq } = useCampaignSequence(id);
  const { data: templatesResp } = useTemplates();
  const save = useSaveCampaignSequence(id ?? '');
  // Toggle to show the full template list (not just the relevant subset).
  // Default off — narrowing keeps the dropdown short for the common case.
  const [showAllTemplates, setShowAllTemplates] = useState(false);

  const [steps, setSteps] = useState<SequenceStep[]>([]);

  useEffect(() => {
    if (existing?.steps) {
      setSteps(existing.steps as SequenceStep[]);
    } else if (!loadingSeq && steps.length === 0) {
      // Default starter step
      setSteps([{ step_number: 1, type: 'EMAIL', subject: '', body_text: '' }]);
    }
  }, [existing, loadingSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!id) return <Navigate to="/campaigns/new" replace />;
  if (loadingCampaign) return <LoadingShell />;
  if (!campaign) return <Navigate to="/campaigns" replace />;
  if (campaign.status !== 'DRAFT') return <Navigate to={`/campaigns/${id}`} replace />;

  const allTemplates = templatesResp?.data ?? [];
  // Filter the dropdown to templates matching the campaign type so the
  // recruiter isn't wading through 30+ irrelevant options:
  //   NEWSLETTER campaign → newsletter templates only.
  //   Everything else     → exclude newsletter templates.
  // "Show all" toggle escapes the narrowing for power users.
  const templates = (() => {
    if (showAllTemplates) return allTemplates;
    if (campaign.type === 'NEWSLETTER') {
      return allTemplates.filter((t) => t.category === 'NEWSLETTER');
    }
    return allTemplates.filter((t) => t.category !== 'NEWSLETTER');
  })();

  const updateStep = (idx: number, patch: Partial<SequenceStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addStep = (type: StepType) => {
    if (steps.length >= MAX_STEPS) return;
    setSteps((prev) => [
      ...prev,
      {
        step_number: prev.length + 1,
        type,
        ...(type === 'DELAY' ? { delay_days: 2 } : {}),
        ...(type === 'EMAIL' ? { subject: '', body_text: '' } : {}),
      },
    ]);
  };

  const removeStep = (idx: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 })));
  };

  const moveStep = (idx: number, direction: -1 | 1) => {
    const target = idx + direction;
    if (target < 0 || target >= steps.length) return;
    setSteps((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      return next.map((s, i) => ({ ...s, step_number: i + 1 }));
    });
  };

  const validate = (): string | null => {
    if (steps.length === 0) return 'Add at least one step before continuing.';
    for (const s of steps) {
      if (s.type === 'EMAIL' && !(s.subject ?? '').trim()) {
        return `Step ${s.step_number}: an email step needs a subject line.`;
      }
      if (s.type === 'EMAIL' && !(s.body_text ?? '').trim() && !s.template_id) {
        return `Step ${s.step_number}: pick a template or write a body.`;
      }
      // Fixed-time delays need a non-negative delay_days; meeting-relative
      // delays use delay_offset_minutes instead and skip this check.
      if (s.type === 'DELAY' && s.delay_anchor !== 'meeting_start' && (s.delay_days == null || s.delay_days < 0)) {
        return `Step ${s.step_number}: delay days must be 0 or more.`;
      }
    }
    return null;
  };

  const persistAndNext = async () => {
    const error = validate();
    if (error) {
      addToast({ variant: 'warning', title: 'Cannot save sequence', description: error });
      return;
    }
    try {
      await save.mutateAsync({ steps });
      addToast({ variant: 'success', title: 'Sequence saved' });
      navigate(stepPath(id, 'settings'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.';
      addToast({ variant: 'danger', title: 'Save failed', description: msg });
    }
  };

  const mode = enrollmentMode(campaign);
  const info = stepInfo('sequence', mode);

  return (
    <WizardLayout step="sequence" campaignId={id} campaign={campaign}>
      <FormCard>
        <FormHeader stepIndex={info.index} totalSteps={info.total} label="Sequence" />

        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
          Build the message flow. Each enrollment moves through these steps in order. Max {MAX_STEPS} steps; replies and
          bounces automatically exit the sequence.
        </p>

        {allTemplates.length > templates.length && (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
              alignSelf: 'flex-start',
            }}
          >
            <input
              type="checkbox"
              checked={showAllTemplates}
              onChange={(e) => setShowAllTemplates(e.target.checked)}
            />
            Show all templates (currently showing {campaign.type === 'NEWSLETTER' ? 'newsletter' : 'non-newsletter'} only, {allTemplates.length - templates.length} hidden)
          </label>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {steps.map((step, idx) => (
            <StepCard
              key={idx}
              step={step}
              index={idx}
              totalSteps={steps.length}
              allSteps={steps}
              templates={templates}
              onChange={(patch) => updateStep(idx, patch)}
              onRemove={() => removeStep(idx)}
              onMove={(d) => moveStep(idx, d)}
            />
          ))}
        </div>

        <AddStepRow onAdd={addStep} disabled={steps.length >= MAX_STEPS} />

        {steps.length >= MAX_STEPS && (
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Sequence is at the {MAX_STEPS}-step maximum.
          </p>
        )}

        <WizardFooter
          onBack={() => navigate(stepPath(id, mode === 'trigger' ? 'triggers' : 'audience'))}
          onNext={persistAndNext}
          nextLabel="Next: Settings"
          isPending={save.isPending}
          primaryDisabled={steps.length === 0}
        />
      </FormCard>
    </WizardLayout>
  );
}

/* ── Step card ── */

function StepCard({
  step,
  index,
  totalSteps,
  allSteps,
  templates,
  onChange,
  onRemove,
  onMove,
}: {
  step: SequenceStep;
  index: number;
  totalSteps: number;
  allSteps: SequenceStep[];
  templates: Template[];
  onChange: (patch: Partial<SequenceStep>) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-input)',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        backgroundColor: 'var(--color-surface-white)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'var(--step-circle-size)',
            height: 'var(--step-circle-size)',
            borderRadius: 'var(--radius-full)',
            backgroundColor: 'var(--color-brand-blue)',
            color: 'var(--color-text-inverse)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            flexShrink: 0,
          }}
        >
          {step.step_number}
        </span>
        <select
          value={step.type}
          onChange={(e) => onChange({ type: e.target.value as StepType })}
          aria-label="Step type"
          style={{
            height: 'var(--btn-height-sm)',
            padding: '0 var(--space-3)',
            border: '1px solid var(--input-border)',
            borderRadius: 'var(--input-radius)',
            backgroundColor: 'var(--input-bg)',
            fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--color-text-primary)',
            cursor: 'pointer',
          }}
        >
          {STEP_TYPE_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <span style={{ flex: 1 }} />
        <SmallIconButton aria-label="Move up" onClick={() => onMove(-1)} disabled={index === 0}>
          ↑
        </SmallIconButton>
        <SmallIconButton aria-label="Move down" onClick={() => onMove(1)} disabled={index === totalSteps - 1}>
          ↓
        </SmallIconButton>
        <SmallIconButton aria-label="Remove step" onClick={onRemove}>
          ✕
        </SmallIconButton>
      </div>

      <StepBody step={step} allSteps={allSteps} templates={templates} onChange={onChange} />
    </div>
  );
}

// Branch condition shape — mirrors the workflow engine's
// SequenceStepJson.branch_condition. Keep in sync.
interface BranchCondition {
  type?: 'replied' | 'opened' | 'clicked';
  on_true?: number | null;
  on_false?: number | null;
}

function StepBody({
  step,
  allSteps,
  templates,
  onChange,
}: {
  step: SequenceStep;
  allSteps: SequenceStep[];
  templates: Template[];
  onChange: (patch: Partial<SequenceStep>) => void;
}) {
  const { data: orgUsers } = useOrgUsers();
  if (step.type === 'EMAIL') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {templates.length > 0 && (
          <select
            value={step.template_id ?? ''}
            onChange={(e) => {
              const tplId = e.target.value || null;
              const tpl = templates.find((t) => t.id === tplId);
              onChange({
                template_id: tplId,
                ...(tpl ? { subject: tpl.subject, body_html: tpl.body_html, body_text: tpl.body_text } : {}),
              });
            }}
            style={{
              height: 'var(--input-height)',
              padding: '0 var(--space-3)',
              border: '1px solid var(--input-border)',
              borderRadius: 'var(--input-radius)',
              backgroundColor: 'var(--input-bg)',
              fontSize: 'var(--input-font-size)',
              fontFamily: 'var(--font-sans)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="">Custom (no template)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
        <SubjectRow
          value={step.subject ?? ''}
          onChange={(v) => onChange({ subject: v })}
        />
        <BodyRow
          value={step.body_text ?? ''}
          onChange={(v) => onChange({ body_text: v, body_html: v.replace(/\n/g, '<br/>') })}
        />
        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Click <code style={inlineCode()}>{'{ }'}</code> to insert a merge field. Paste any URL
          to add a link. <code style={inlineCode()}>{'{{calendar_url}}'}</code> resolves to your
          default booking link at send time, <code style={inlineCode()}>{'{{unsubscribe_url}}'}</code>{' '}
          to the opt-out for newsletter sends.
        </p>
      </div>
    );
  }

  if (step.type === 'SMS') {
    return <SmsStepBody step={step} onChange={onChange} />;
  }

  if (step.type === 'DELAY') {
    return <DelayStepBody step={step} onChange={onChange} />;
  }

  if (step.type === 'TASK') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <TokenField
          label="Task title"
          value={step.task_title ?? ''}
          onChange={(next) => onChange({ task_title: next })}
          tokens={TASK_MERGE_TOKENS}
          placeholder="Task title. Click { } to insert a personalisation tag"
          style={inlineInputStyle()}
        />
        <TaskAssigneeSelect
          value={step.task_assignee_id}
          onChange={(next) => onChange({ task_assignee_id: next })}
          users={orgUsers}
          style={inlineInputStyle()}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          Due in
          <input
            type="number"
            min={0}
            max={60}
            value={step.task_due_days ?? 2}
            onChange={(e) => onChange({ task_due_days: parseInt(e.target.value, 10) || 0 })}
            style={{
              ...inlineInputStyle(),
              width: '80px',
              textAlign: 'center',
            }}
          />
          business {step.task_due_days === 1 ? 'day' : 'days'}
        </label>
      </div>
    );
  }

  if (step.type === 'BRANCH') {
    return <BranchStepBody step={step} allSteps={allSteps} onChange={onChange} />;
  }

  return (
    <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
      {STEP_TYPE_OPTIONS.find((o) => o.id === step.type)?.description}
    </p>
  );
}

/* ── DELAY step ── */

// Pick the friendliest unit for an offset expressed in minutes.
function splitOffset(totalMinutes: number): { amount: number; unit: 'minutes' | 'hours' | 'days' } {
  const abs = Math.abs(totalMinutes);
  if (abs !== 0 && abs % 1440 === 0) return { amount: abs / 1440, unit: 'days' };
  if (abs !== 0 && abs % 60 === 0) return { amount: abs / 60, unit: 'hours' };
  return { amount: abs, unit: 'minutes' };
}
const UNIT_MINUTES: Record<'minutes' | 'hours' | 'days', number> = { minutes: 1, hours: 60, days: 1440 };

function DelayStepBody({
  step,
  onChange,
}: {
  step: SequenceStep;
  onChange: (patch: Partial<SequenceStep>) => void;
}) {
  const isMeeting = step.delay_anchor === 'meeting_start';
  const offset = step.delay_offset_minutes ?? -60; // default: 1 hour before
  const { amount, unit } = splitOffset(offset);
  const direction: 'before' | 'after' = offset <= 0 ? 'before' : 'after';

  const setMeetingOffset = (next: { amount?: number; unit?: 'minutes' | 'hours' | 'days'; direction?: 'before' | 'after' }) => {
    const a = next.amount ?? amount;
    const u = next.unit ?? unit;
    const d = next.direction ?? direction;
    const minutes = Math.max(0, a) * UNIT_MINUTES[u] * (d === 'before' ? -1 : 1);
    onChange({ delay_offset_minutes: minutes });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <label style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <select
          value={isMeeting ? 'meeting' : 'fixed'}
          onChange={(e) =>
            e.target.value === 'meeting'
              ? onChange({ delay_anchor: 'meeting_start', delay_offset_minutes: step.delay_offset_minutes ?? -60 })
              : onChange({ delay_anchor: null })
          }
          style={{ ...inlineInputStyle(), width: 'auto', marginRight: 'var(--space-2)' }}
        >
          <option value="fixed">Wait a fixed time</option>
          <option value="meeting">Wait relative to the booked meeting</option>
        </select>
      </label>

      {!isMeeting ? (
        <label style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          Wait
          <input
            type="number"
            min={0}
            max={60}
            value={step.delay_days ?? 0}
            onChange={(e) => onChange({ delay_days: parseInt(e.target.value, 10) || 0 })}
            style={{ ...inlineInputStyle(), width: '80px', margin: '0 var(--space-2)', textAlign: 'center' }}
          />
          business {step.delay_days === 1 ? 'day' : 'days'}
        </label>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            Send
            <input
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setMeetingOffset({ amount: parseInt(e.target.value, 10) || 0 })}
              style={{ ...inlineInputStyle(), width: '72px', textAlign: 'center' }}
            />
            <select value={unit} onChange={(e) => setMeetingOffset({ unit: e.target.value as 'minutes' | 'hours' | 'days' })} style={{ ...inlineInputStyle(), width: 'auto' }}>
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
            <select value={direction} onChange={(e) => setMeetingOffset({ direction: e.target.value as 'before' | 'after' })} style={{ ...inlineInputStyle(), width: 'auto' }}>
              <option value="before">before</option>
              <option value="after">after</option>
            </select>
            the booked meeting
          </div>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', margin: 0 }}>
            The next step (email/SMS) fires at that time. Only applies to campaigns enrolled by a “Meeting booked”
            trigger; other enrollments advance immediately.
          </p>
        </div>
      )}
    </div>
  );
}

function SmsStepBody({
  step,
  onChange,
}: {
  step: SequenceStep;
  onChange: (patch: Partial<SequenceStep>) => void;
}) {
  const body = step.body_text ?? '';
  const len = body.length;
  const overCap = len > SMS_BODY_MAX;
  // What the recipient gets: the opt-out line is added at send unless the agency turned it off.
  const footerOn = useOrgSettings().data?.data?.sms_opt_out_footer ?? true;
  const sent = withOptOutFooter(body, footerOn);
  const segments = smsSegments(sent);
  const multiSegment = segments > 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <TokenField
        label="Text message"
        multiline
        value={body}
        onChange={(next) => onChange({ body_text: next, subject: null, body_html: null })}
        tokens={SMS_MERGE_TOKENS}
        placeholder="Text message. Click { } to insert a personalisation tag. Recipients always see the campaign's number."
        rows={3}
        maxLength={SMS_BODY_MAX}
        style={{
          ...inlineInputStyle(),
          height: 'auto',
          minHeight: 'calc(var(--input-height) * 1.8)',
          paddingTop: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          resize: 'vertical',
          lineHeight: 'var(--leading-normal)',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Preview: {body.trim() ? <span style={{ whiteSpace: 'pre-wrap' }}>{sent}</span> : <em>(empty, required before launch)</em>}
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-xs)',
          color: overCap ? 'var(--color-warning)' : multiSegment ? 'var(--color-text-secondary)' : 'var(--color-text-muted)',
        }}>
          {len} / {SMS_BODY_MAX} chars · {segments} segment{segments === 1 ? '' : 's'}
          {multiSegment && !overCap && ' (costs 2+ messages)'}
        </span>
      </div>
    </div>
  );
}

function BranchStepBody({
  step,
  allSteps,
  onChange,
}: {
  step: SequenceStep;
  allSteps: SequenceStep[];
  onChange: (patch: Partial<SequenceStep>) => void;
}) {
  const bc = (step.branch_condition as BranchCondition | undefined) ?? {};

  // Only later steps in the sequence can be jump targets — referencing an
  // earlier step would loop, which the workflow engine doesn't support yet.
  const downstreamSteps = allSteps.filter((s) => s.step_number > step.step_number);

  const patchBranch = (patch: Partial<BranchCondition>) => {
    onChange({ branch_condition: { ...bc, ...patch } as never });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        If the contact has
        <select
          value={bc.type ?? ''}
          onChange={(e) => patchBranch({ type: (e.target.value || undefined) as BranchCondition['type'] })}
          style={{ ...inlineInputStyle(), width: '160px', cursor: 'pointer' }}
        >
          <option value="">Pick condition</option>
          <option value="replied">Replied</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
        </select>
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        Yes → jump to
        <select
          value={bc.on_true?.toString() ?? ''}
          onChange={(e) => patchBranch({ on_true: e.target.value ? parseInt(e.target.value, 10) : null })}
          disabled={downstreamSteps.length === 0}
          style={{ ...inlineInputStyle(), width: '200px', cursor: 'pointer' }}
        >
          <option value="">Next step</option>
          {downstreamSteps.map((s) => (
            <option key={s.step_number} value={s.step_number}>
              Step {s.step_number} · {s.type}
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        No → jump to
        <select
          value={bc.on_false?.toString() ?? ''}
          onChange={(e) => patchBranch({ on_false: e.target.value ? parseInt(e.target.value, 10) : null })}
          disabled={downstreamSteps.length === 0}
          style={{ ...inlineInputStyle(), width: '200px', cursor: 'pointer' }}
        >
          <option value="">Next step</option>
          {downstreamSteps.map((s) => (
            <option key={s.step_number} value={s.step_number}>
              Step {s.step_number} · {s.type}
            </option>
          ))}
        </select>
      </label>

      {downstreamSteps.length === 0 && (
        <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Add more steps below this one to enable routing targets.
        </p>
      )}
    </div>
  );
}

/* ── Add step row ── */

function AddStepRow({ onAdd, disabled }: { onAdd: (type: StepType) => void; disabled: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Add a step:
      </span>
      {STEP_TYPE_OPTIONS.map((o) => (
        <AddStepButton key={o.id} onClick={() => onAdd(o.id)} disabled={disabled}>
          + {o.label}
        </AddStepButton>
      ))}
    </div>
  );
}

function AddStepButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-action-secondary-text)',
        border: '1px solid var(--color-action-secondary-border)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function SmallIconButton({ children, onClick, disabled, 'aria-label': ariaLabel }: { children: ReactNode; onClick?: () => void; disabled?: boolean; 'aria-label': string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        width: 'var(--btn-height-sm)',
        height: 'var(--btn-height-sm)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'transparent',
        border: '1px solid var(--color-border-default)',
        color: 'var(--color-text-muted)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

function inlineInputStyle(): React.CSSProperties {
  return {
    height: 'var(--input-height)',
    padding: 'var(--input-padding-y) var(--input-padding-x)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--input-radius)',
    backgroundColor: 'var(--input-bg)',
    fontSize: 'var(--input-font-size)',
    fontFamily: 'var(--font-sans)',
    color: 'var(--color-text-primary)',
    outline: 'none',
  };
}

function inlineCode(): React.CSSProperties {
  return {
    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
    fontSize: 'var(--text-xs)',
    background: 'var(--color-surface-muted, #f7f8fc)',
    padding: '1px 6px',
    borderRadius: 3,
  };
}

/* ── Subject input + merge-field inserter ────────────────────────────── */

function SubjectRow({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const [caret, setCaret] = useState(value.length);
  return (
    <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? value.length)}
        placeholder="Subject line: click { } to insert a merge field"
        style={{ ...inlineInputStyle(), flex: 1 }}
      />
      <MergeFieldInserter
        tokens={COMMON_MERGE_TOKENS}
        onInsert={onChange}
        value={value}
        caret={caret}
      />
    </div>
  );
}

/* ── Body textarea + merge-field inserter ────────────────────────────── */

function BodyRow({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const [caret, setCaret] = useState(value.length);
  return (
    <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'flex-start' }}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? value.length)}
        placeholder="Body text: click { } to insert a merge field"
        // 12 rows / ~7.2× input-height — sized so a loaded template's
        // body fits without scrolling. Resize:vertical lets the user
        // shrink it back if they want.
        rows={12}
        style={{
          ...inlineInputStyle(),
          flex: 1,
          height: 'auto',
          minHeight: 'calc(var(--input-height) * 7.2)',
          paddingTop: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          resize: 'vertical',
          lineHeight: 'var(--leading-normal)',
        }}
      />
      <MergeFieldInserter
        tokens={COMMON_MERGE_TOKENS}
        onInsert={onChange}
        value={value}
        caret={caret}
      />
    </div>
  );
}

function LoadingShell() {
  return (
    <WizardLayout step="sequence">
      <FormCard>
        <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-muted)' }}>Loading campaign…</p>
      </FormCard>
    </WizardLayout>
  );
}
